import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = /*#__PURE__*/_createTextVNode("Type: ")
const _hoisted_2 = /*#__PURE__*/_createTextVNode("Severity: ")
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", null, "Comment: ", -1)

import { useGetInspectionGroupQuery } from '@/entities/inspection-group/hooks';
import { orderBy } from 'lodash';
import { required } from '@vuelidate/validators';
import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  reactive,
  Ref,
  ComputedRef,
  ref,
  watch,
} from 'vue';

import {
  NkBadge,
  NkButton,
  NkForm,
  NkFormItem,
  NkGroup,
  NkOption,
  NkSelect,
  NkStack,
  NkText,
  NkTextArea,
} from '@/core/components';

import { useCreateAnnotationGroupMutation } from '@/entities/annotation-group/hooks';
import { useGetInspectionQuery } from '@/entities/inspection/hooks';
import {
  useGetDeviceQuery,
  useGetNestedDevicesQuery,
} from '@/entities/device/hooks';
import { useGetTypesQuery } from '@/entities/type/hooks';
import {
  useCreateAnnotationMutation,
} from '@/entities/annotation/hooks';

import { useForm } from '@/core/hooks/forms';

import { Device } from '@/entities/device/types';
import { AnnotationSeverity } from '@/entities/annotation-severity/types';
import { Type } from '@/entities/type/types';
import { Annotation } from '@/entities/annotation/types';
import { AnnotationType } from '@/entities/annotation-type/types';

import {
  events,
  InspectionViewerCreateAnnotationFormInjection,
  inspectionViewerCreateAnnotationFormInjectionKey,
  InspectionViewerInjection,
  inspectionViewerInjectionKey,
} from '@/teams/visionair/components/InspectionViewer/inspection-viewer.shared';

import AnnotationSeveritySelect
  from '@/entities/annotation-severity/components/inputs/AnnotationSeveritySelect.vue';
import AnnotationTypeSelect
  from '@/entities/annotation-type/components/inputs/AnnotationTypeSelect.vue';
import AnnotationLinkSelect
  from '@/entities/annotation/components/inputs/AnnotationLinkSelect.vue';
import { ElNotification } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { useGetAnnotationSeverityQuery } from '@/entities/annotation-severity/hooks';
import { useGetAnnotationTypeQuery } from '@/entities/annotation-type/hooks';


export default /*#__PURE__*/_defineComponent({
  __name: 'InspectionViewerCreateAnnotationLinkForm',
  setup(__props) {

const { t, locale } = useI18n();

const injectedInspectionViewerCreateAnnotationForm = inject(inspectionViewerCreateAnnotationFormInjectionKey) as InspectionViewerCreateAnnotationFormInjection;
const injectedInspectionViewer = inject(inspectionViewerInjectionKey) as InspectionViewerInjection;

const { props } = injectedInspectionViewerCreateAnnotationForm;
const { onSubmitted } = useForm(injectedInspectionViewerCreateAnnotationForm.emit);

const linkAnnotationForm = ref();
const isLoading = ref(false);

const { mutateAsync: createAnnotationMutation } = useCreateAnnotationMutation();
const { mutateAsync: createAnnotationGroupMutation } = useCreateAnnotationGroupMutation();

const fields: {
  device: Device['id'],
  annotation: Annotation['id'] | null
  type: Type['id'] | null,
  severity: AnnotationSeverity['id'] | null,
  comment: string
} = reactive({
  device: props.device,
  annotation: null,
  type: null,
  severity: null,
  comment: '',
});

const annotationLinkSelect = ref();
const annotationLinkSelectAnnotation: ComputedRef<Annotation | undefined> = computed(() => annotationLinkSelect.value?.selectedAnnotation);

const ruleObject = {
  device: { required },
  annotation: { required },
};

const rules = computed(() => {
  if (!annotationLinkSelectAnnotation.value) {
    return ruleObject;
  }

  return {
    ...ruleObject,
    type: { required },
    severity: { required },
  };
});

const inspectionQueryData = computed(() => ({ id: props.inspection }));
const hasInspectionQueryData: Ref = computed(() => !!inspectionQueryData.value.id);
const { data: inspection } = useGetInspectionQuery(inspectionQueryData, {
  refetchOnWindowFocus: false,
  enabled: hasInspectionQueryData,
});

const inspectionGroupQueryData: Ref = computed(() => ({ id: inspection.value?.inspectionGroup }));
const hasInspectionGroupQueryData = computed(() => !!inspectionGroupQueryData.value);
const { data: inspectionGroupData } = useGetInspectionGroupQuery(inspectionGroupQueryData, { enabled: hasInspectionGroupQueryData });

const getDeviceQueryData = computed(() => ({ id: props.device }));
const hasDeviceData = computed(() => !!getDeviceQueryData.value.id);
const { data: device } = useGetDeviceQuery(getDeviceQueryData, { enabled: hasDeviceData });

const getParentDeviceData = computed(() => ({ id: device.value?.parent || -1 }));
const hasParentDeviceData = computed(() => !!device.value);
const { data: devicesData } = useGetNestedDevicesQuery(getParentDeviceData, { enabled: hasParentDeviceData });
const devices = computed(() => (devicesData.value?.filter(o => o.inspections.length) || []));

const getAnnotationSeverityData = computed(() => ({ id: annotationLinkSelectAnnotation.value?.severity }))
const hasAnnotationSeverityData = computed(() => !!getAnnotationSeverityData.value.id)
const {data: annotationSeverity, isFetching: fetchingSeverity} = useGetAnnotationSeverityQuery(getAnnotationSeverityData, {
  enabled: hasAnnotationSeverityData,
  refetchOnWindowFocus: false
})

const getAnnotationTypeData = computed(() => ({ id: annotationLinkSelectAnnotation.value?.severity }))
const hasAnnotationTypeData = computed(() => !!getAnnotationSeverityData.value.id)
const {data: annotationType, isFetching: fetchingType} = useGetAnnotationTypeQuery(getAnnotationTypeData, {
  enabled: hasAnnotationTypeData,
  refetchOnWindowFocus: false
})

const hasDevices = computed(() => !!devices.value.length);
const getTypesFilters = computed(() => ({
  ids: devices.value.map(d => d.type),
}));
const { data: typesData } = useGetTypesQuery({
  enabled: hasDevices,
}, getTypesFilters);
const types = computed(() => orderBy((typesData.value || []), 'name', 'asc'));

const deviceValues = computed(() => devices.value.map((d) => ({
  value: d.id,
  label: types.value.find(ty => ty.id === d.type)?.name || '',
  id: types.value.find(ty => ty.id === d.type)?.id,
})));

const updateActiveAnnotation = () => {
  injectedInspectionViewer.setHoveredAnnotation(fields.annotation);
};

watch(() => fields.annotation, () => {
  if (fields.device === inspection.value?.device) {
    updateActiveAnnotation();
  }
});

watch(annotationLinkSelectAnnotation, (value) => {
  if (value) {
    fields.type = value.type;
    fields.severity = value.severity;
    fields.comment = value.expertComment[locale.value] !== null ? value.expertComment[locale.value] : value.comment
  }
});

const linkAnnotation = () => {
  if (!isLoading.value) {
    linkAnnotationForm.value.validator.$validate()
      .then((valid: boolean) => {
        if (valid) {
          isLoading.value = true;

          const {
            comment,
            expertComment,
            annotationGroupTypes,
            id: linkedAnnotationId,
          } = (annotationLinkSelectAnnotation.value as Annotation);

          createAnnotationMutation({
            comment,
            expertComment,
            annotationGroups: annotationGroupTypes?.normal ? [annotationGroupTypes.normal] : [],
            inspection: props.inspection,
            severity: fields.severity as AnnotationSeverity['id'],
            type: fields.type as AnnotationType['id'],
            annotationImages: [],
            createdBy: null,
            position: {
              device: props.device,
              position: 0,
              pixelX: Math.round(props.completeLocation.pixelLocation.x),
              pixelY: Math.round(props.completeLocation.pixelLocation.y),
              pixelWidth: Math.round(props.completeLocation.pixelLocation.width),
              pixelHeight: Math.round(props.completeLocation.pixelLocation.height),
              viewportX: props.completeLocation.viewportLocation.x,
              viewportY: props.completeLocation.viewportLocation.y,
              viewportWidth: props.completeLocation.viewportLocation.width,
              viewportHeight: props.completeLocation.viewportLocation.height,
              hubHeight: 0,
            },
            status: 'to fix'
          },
            {
              onSuccess: () => {
                ElNotification({
                  title: t('annotation.notification.add-link.success.title'),
                  message: t('annotation.notification.add-link.success.message'),
                  type: 'success',
                  position: 'bottom-right',
                });
              }
            }).then((res) => {
            if (!annotationGroupTypes?.normal) {
              createAnnotationGroupMutation({
                date: new Date(),
                comment: '',
                type: 'normal',
                ids: [linkedAnnotationId, res.id],
              }).then(() => {
                onSubmitted();
              }).catch(() => {
                console.log('Error on Annotation Group creation');
              }).finally(() => {
                isLoading.value = false;
              });
            } else {
              onSubmitted();
            }
          }).catch(() => {
            console.log('Error on Annotation creation');
            isLoading.value = false;
          });
        }
      });
  }
};

const centerView = () => {
  injectedInspectionViewer.centerFormView(props.completeLocation.viewportLocation);
};

onMounted(() => {
  fields.device = props.device;
  centerView();

  events.on('annotation-click', (e) => {
    fields.annotation = e.annotation;
    fields.device = e.device;
  });

  updateActiveAnnotation();
});

onBeforeUnmount(() => {
  if (injectedInspectionViewer) {
    injectedInspectionViewer.setHoveredAnnotation(null);
  }
});

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(_unref(NkForm), {
    ref_key: "linkAnnotationForm",
    ref: linkAnnotationForm,
    fields: fields,
    rules: _unref(rules)
  }, {
    footer: _withCtx(() => [
      _createVNode(_unref(NkGroup), { position: "right" }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkButton), { onClick: linkAnnotation }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('annotation.action.linkToAnnotation')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(NkFormItem), {
        label: _ctx.$t('misc.device'),
        prop: "device"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkSelect), {
            onChange: _cache[0] || (_cache[0] = ($event: any) => (fields.annotation = null)),
            modelValue: fields.device,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((fields.device) = $event))
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(deviceValues), (option) => {
                return (_openBlock(), _createBlock(_unref(NkOption), {
                  key: option.id,
                  label: option.label,
                  value: option.value
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(option.label), 1)
                  ]),
                  _: 2
                }, 1032, ["label", "value"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkFormItem), {
        label: _ctx.$t('misc.annotation'),
        prop: "annotation"
      }, {
        default: _withCtx(() => [
          _createVNode(AnnotationLinkSelect, {
            ref_key: "annotationLinkSelect",
            ref: annotationLinkSelect,
            "model-value": fields.annotation,
            device: fields.device,
            inspection: _unref(props).inspection,
            annotation: fields.annotation || null,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = (value) => fields.annotation = value.id)
          }, null, 8, ["model-value", "device", "inspection", "annotation"])
        ]),
        _: 1
      }, 8, ["label"]),
      (_unref(annotationLinkSelectAnnotation) && _unref(rules).severity && _unref(rules).type && !_unref(fetchingSeverity) && !_unref(fetchingType))
        ? (_openBlock(), _createBlock(_unref(NkStack), {
            key: 0,
            class: "mt-2",
            spacing: 5
          }, {
            default: _withCtx(() => [
              _createElementVNode("p", null, [
                _hoisted_1,
                _createVNode(_unref(NkText), {
                  as: "span",
                  type: "bold"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(annotationType).name), 1)
                  ]),
                  _: 1
                })
              ]),
              _createElementVNode("p", null, [
                _hoisted_2,
                _createVNode(_unref(NkBadge), {
                  plain: true,
                  size: "sm",
                  color: _unref(annotationSeverity)?.color
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(annotationSeverity).name), 1)
                  ]),
                  _: 1
                }, 8, ["color"])
              ]),
              _createElementVNode("div", null, [
                _hoisted_3,
                _createVNode(_unref(NkText), {
                  align: "justify",
                  type: "semibold-xs"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(fields.comment), 1)
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["fields", "rules"])), [
    [_directive_loading, isLoading.value]
  ])
}
}

})